import { Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { InsuranceAdjustor } from '../types';

interface AdjustorListProps {
  adjustors: InsuranceAdjustor[];
  onEdit: (adjustor: InsuranceAdjustor) => void;
  onDeactivate: (adjustor: InsuranceAdjustor) => void;
  isLoading?: boolean;
}

export function AdjustorList({
  adjustors,
  onEdit,
  onDeactivate,
  isLoading = false,
}: AdjustorListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D71F20] mx-auto" />
        <p className="mt-3 text-[#84878A] text-sm">Loading adjustors...</p>
      </div>
    );
  }

  if (adjustors.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No adjustors found for this company.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Office Phone</TableHead>
            <TableHead>Cell Phone</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {adjustors.map((adjustor) => (
            <TableRow key={adjustor.id}>
              <TableCell className="font-medium">{adjustor.fullName}</TableCell>
              <TableCell>{adjustor.officePhone || '-'}</TableCell>
              <TableCell>{adjustor.cellPhone || '-'}</TableCell>
              <TableCell>{adjustor.email || '-'}</TableCell>
              <TableCell className="text-center">
                <Badge variant={adjustor.isActive ? 'default' : 'secondary'}>
                  {adjustor.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(adjustor)}
                    title="Edit adjustor"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {adjustor.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeactivate(adjustor)}
                      title="Deactivate adjustor"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
